import { useState } from "react";
import { Box } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import ElevatedPaper from "../../../../components/ElevatedPaper";
import CustomTabs from "../../../../components/app/CustomTabs";
import { FilterSelect } from "../../../../components/FilterSelect";
import Flex from "../../../../components/Flex";
import PageLayout from "../../../../components/PageLayout";
import { TabData } from "../../../../components/app/CustomTabs";
import AppointmentSchedulingReport from "./appointment-scheduling/AppointmentSchedulingReport";
import AppointmentReportFilters from "./appointment-scheduling/AppointmentReportFilters";
import PatientEncounterReport from "./patient-encounter/PatientEncounterReport";
import PatientEncounterFilters from "./patient-encounter/PatientEncounterFilters";
import ReportExport from "./ReporExport";
import {
  REPORT_TYPE_OPTIONS,
  AppointmentReportFilterData,
  DEFAULT_REPORT_FILTERS,
} from "./appointment-scheduling/appointment-report-constants";
import {
  PatientEncounterFilterData,
  DEFAULT_ENCOUNTER_FILTERS,
} from "./patient-encounter/patient-encounter-constants";
import BillingSchedulingReport from "./billing/BillingSchedulingReport";
import BillingReportFilters from "./billing/BillingReportFilters";
import {
  BillingReportFilterData,
  DEFAULT_BILLING_FILTERS,
} from "./billing/billing-report-constants";
import { useAppointmentExportCsv } from "../../../../queries/use-report-export";
import { useEncounterExportCsv } from "../../../../queries/patient-encounter/use-encounter-export-csv";
import { useClaimExportCsv } from "../../../../queries/billings/claims/use-claim-export-csv";

const REPORT_TABS: TabData[] = [
  { value: "clinical", label: "Clinical" },
  { value: "billing", label: "Billing" },
];

const BILLING_REPORT_TYPE_OPTIONS = [
  {
    value: "electronic-claim-submission",
    label: "Electronic Claim Submission",
  },
];

const ReportsLayout = () => {
  const [activeTab, setActiveTab] = useState("clinical");
  const [reportType, setReportType] = useState<string | null>(
    "patient-encounters"
  );
  const [billingReportType, setBillingReportType] = useState<string | null>(
    "electronic-claim-submission"
  );
  const [filters, setFilters] = useState<AppointmentReportFilterData>(
    DEFAULT_REPORT_FILTERS
  );
  const [encounterFilters, setEncounterFilters] =
    useState<PatientEncounterFilterData>(DEFAULT_ENCOUNTER_FILTERS);
  const [billingFilters, setBillingFilters] = useState<BillingReportFilterData>(
    DEFAULT_BILLING_FILTERS
  );
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const { exportAppointmentsCsv } = useAppointmentExportCsv();
  const { exportEncountersCsv } = useEncounterExportCsv();
  const { exportClaimsCsv } = useClaimExportCsv();

  const handleExport = (type: string) => {
    const selectedIds = rowSelectionModel as string[];
    if (activeTab === "clinical") {
      if (reportType === "appointment-scheduling") {
        exportAppointmentsCsv(selectedIds);
      } else if (reportType === "patient-encounters") {
        exportEncountersCsv(selectedIds);
      }
    } else if (type === "CSV") {
      exportClaimsCsv(selectedIds);
    }
  };

  const handleFilterApply = (newFilters: AppointmentReportFilterData) => {
    setFilters(newFilters);
  };

  const handleBillingFilterApply = (newFilters: BillingReportFilterData) => {
    setBillingFilters(newFilters);
  };

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    setRowSelectionModel([]);
  };

  const currentReportTypeOptions =
    activeTab === "clinical"
      ? REPORT_TYPE_OPTIONS
      : BILLING_REPORT_TYPE_OPTIONS;
  const currentReportType =
    activeTab === "clinical" ? reportType : billingReportType;
  const setCurrentReportType =
    activeTab === "clinical" ? setReportType : setBillingReportType;

  return (
    <ElevatedPaper>
      <PageLayout
        header={
          <Flex
            justifyContent="space-between"
            alignItems="center"
            pb={1.5}
            borderBottom="1px solid #E7E7E7"
          >
            <CustomTabs
              tabsData={REPORT_TABS}
              value={activeTab}
              onChange={handleTabChange}
            />
            <Flex gap="16px" alignItems="center">
              {activeTab === "clinical" &&
                reportType === "appointment-scheduling" && (
                  <AppointmentReportFilters
                    filters={filters}
                    onApply={handleFilterApply}
                  />
                )}
              {activeTab === "clinical" &&
                reportType === "patient-encounters" && (
                  <PatientEncounterFilters
                    filters={encounterFilters}
                    onApply={setEncounterFilters}
                  />
                )}
              {activeTab === "billing" &&
                billingReportType === "electronic-claim-submission" && (
                  <BillingReportFilters
                    filters={billingFilters}
                    onApply={handleBillingFilterApply}
                  />
                )}
              <Box width="18rem">
                <FilterSelect
                  label="Report Type"
                  options={currentReportTypeOptions}
                  value={currentReportType}
                  onChange={(val) => {
                    setCurrentReportType(val);
                    setRowSelectionModel([]);
                  }}
                  placeholder="Select Report Type"
                  clearable={false}
                />
              </Box>
              <ReportExport
                onClick={handleExport}
                disabled={rowSelectionModel.length === 0}
              />
            </Flex>
          </Flex>
        }
      >
        {/* Report Content */}
        {activeTab === "clinical" && (
          <>
            {reportType === "appointment-scheduling" && (
              <AppointmentSchedulingReport
                filters={filters}
                onFilterChange={setFilters}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
              />
            )}
            {reportType === "patient-encounters" && (
              <PatientEncounterReport
                filters={encounterFilters}
                onFilterChange={setEncounterFilters}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
              />
            )}
            {!reportType && (
              <Flex
                justifyContent="center"
                alignItems="center"
                height="50vh"
                color="#999"
              >
                Please select a report type
              </Flex>
            )}
          </>
        )}

        {activeTab === "billing" && (
          <>
            {billingReportType === "electronic-claim-submission" && (
              <BillingSchedulingReport
                filters={billingFilters}
                onFilterChange={setBillingFilters}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
              />
            )}
            {!billingReportType && (
              <Flex
                justifyContent="center"
                alignItems="center"
                height="50vh"
                color="#999"
              >
                Please select a report type
              </Flex>
            )}
          </>
        )}
      </PageLayout>
    </ElevatedPaper>
  );
};

export default ReportsLayout;
